/**
 * Octopus IoT Platform — TripReportPrint
 *
 * Página de impressão por viagem. Gera PDF via html2canvas + jsPDF.
 * Nunca usa a API de impressão nativa do navegador (incompatível com Recharts dinâmico).
 * Rota: /operations/trips/:tripId/print — gated por trip:read.
 * INV-UI-TRAIL-001: zero recompute de telemetria; só exibição dos campos do backend.
 * REQ-32: gráfico de consumo por motor (Recharts BarChart).
 * REQ-15: relatório PDF por viagem.
 *
 * Padrão de captura (espelhar EquipmentMonitorPrint.js):
 *   - Wrapper A4 retrato (210mm) visível na tela → Recharts mede dimensões reais.
 *   - html2canvas scale=2.5 → toDataURL jpeg 0.92 → jsPDF portrait a4.
 *   - Multi-página: loop desloca offset Y a cada 297mm.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { FileText, Loader2, ChevronLeft, AlertCircle, Download } from 'lucide-react';
import { tripsApi } from '@/services/tripsApi';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useAuth } from '@/contexts/AuthContext';

// ---------------------------------------------------------------------------
// Helpers (espelhar TripDetailPage.jsx)
// ---------------------------------------------------------------------------

const MOTOR_LABEL = {
  engine1: 'Draga',
  port: 'Bombordo',
  starboard: 'Boreste',
  center: 'Central',
  main: 'Principal',
};

const STAGE_LABEL = {
  carregando: 'Carregando',
  descarregando: 'Descarregando',
  parado: 'Parado',
  viagem_carregado: 'Em deslocamento (carregado)',
  viagem_vazio: 'Em deslocamento (vazio)',
  viagem: 'Em deslocamento',
};

/** Deriva id curto do motor da chave canônica ou id curto (TripDetailPage l.167). */
const shortMotor = (key) => {
  if (!key) return key;
  if (key.includes('.')) {
    const parts = key.split('.');
    return parts.length >= 2 ? parts[1] : key;
  }
  return key;
};

/** Rótulo legível do motor para exibição / eixo X do gráfico. */
const motorLabel = (key) => {
  const short = shortMotor(key);
  return MOTOR_LABEL[short] || MOTOR_LABEL[key] || key;
};

/** Compatibilidade backward: docs antigos com subdir=ida/volta (TripDetailPage l.89). */
const LEGACY_CARGO = { ida: 'carregado', volta: 'vazio' };
const cargoOf = (o) => o?.cargo_state || LEGACY_CARGO[o?.subdir] || null;

/** Chave de rótulo de etapa para um segmento de _stages. */
const stageKey = (seg) => {
  const cs = cargoOf(seg);
  return seg.activity === 'viagem' && cs ? `viagem_${cs}` : seg.activity;
};

/**
 * Formata duração em segundos para "Xh Ymin".
 * Apresentação pura — NÃO recomputa telemetria (INV-UI-TRAIL-001).
 */
const fmtDuration = (s) => {
  if (s == null || !Number.isFinite(Number(s))) return '—';
  const total = Math.round(Number(s));
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  if (hh > 0) return `${hh}h ${mm}min`;
  if (mm > 0) return `${mm}min`;
  return `${total}s`;
};

/**
 * Formata litros (milhar pt-BR). null/undefined → '—'.
 * NÃO calcula — INV-UI-TRAIL-001.
 */
const fmtLitros = (v) =>
  v == null ? '—' : `${Math.round(v).toLocaleString('pt-BR')} L`;

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

function TripReportPrint() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { formatDateTime, timezone } = useTimezone();
  const { user } = useAuth();

  const [trip, setTrip] = useState(null);
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  // null | 'not_found' (404 — não encontrada OU sem acesso, stealth) | 'server_error'
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);

  const wrapperRef = useRef(null);

  // Carrega dados no mount: getTrip + getWeatherBriefing em paralelo.
  // Clima ausente não bloqueia o PDF (catch individual retorna null).
  useEffect(() => {
    if (!tripId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    let tripError = null;

    Promise.all([
      tripsApi.getTrip(tripId).catch((err) => {
        if (!cancelled) {
          const status = err?.response?.status;
          tripError = status === 404 ? 'not_found' : 'server_error';
        }
        return null;
      }),
      tripsApi.getWeatherBriefing(tripId).catch(() => null),
    ]).then(([tripRes, weatherRes]) => {
      if (cancelled) return;
      if (tripError) setError(tripError);
      if (tripRes?.data) setTrip(tripRes.data);
      if (weatherRes?.data) setWeather(weatherRes.data);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [tripId]);

  // ---------------------------------------------------------------------------
  // Geração do PDF via html2canvas + jsPDF (sem print nativo — INV-REQ-32)
  // ---------------------------------------------------------------------------

  const generatePdf = useCallback(async () => {
    const node = wrapperRef.current;
    if (!node || generating) return;
    setGenerating(true);
    setGenerateError(null);
    try {
      const canvas = await html2canvas(node, {
        scale: 2.5,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        letterRendering: true,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();   // 210mm (retrato)
      const pageHeight = pdf.internal.pageSize.getHeight(); // 297mm (retrato)
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight <= pageHeight) {
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
      } else {
        // Multi-página: desloca o offset Y negativo a cada página adicional.
        let heightLeft = imgHeight;
        let position = 0;
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        while (heightLeft > 0) {
          position -= pageHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
      }

      pdf.save(`relatorio_viagem_${trip?.id || tripId}.pdf`);
    } catch (err) {
      console.error('Falha ao gerar PDF:', err);
      setGenerateError('Não foi possível gerar o PDF. Tente novamente.');
    } finally {
      setGenerating(false);
    }
  }, [generating, trip, tripId]);

  // ---------------------------------------------------------------------------
  // Render de estados de carregamento / erro
  // ---------------------------------------------------------------------------

  const ToolbarOnly = ({ children }) => (
    <div className="trip-print-overlay">
      <style>{TRIP_PRINT_CSS}</style>
      <div className="trip-print-toolbar no-print">
        <button
          type="button"
          className="print-btn print-btn--ghost"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className="h-4 w-4" />
          Voltar
        </button>
      </div>
      <div className="trip-center-msg">{children}</div>
    </div>
  );

  if (loading) {
    return (
      <ToolbarOnly>
        <Loader2 className="h-6 w-6 trip-spin" />
        <span>Carregando viagem…</span>
      </ToolbarOnly>
    );
  }

  if (error === 'not_found' || !trip) {
    return (
      <ToolbarOnly>
        <AlertCircle className="h-6 w-6 trip-error-icon" />
        <span>Viagem não encontrada ou acesso não autorizado.</span>
      </ToolbarOnly>
    );
  }

  if (error === 'server_error') {
    return (
      <ToolbarOnly>
        <AlertCircle className="h-6 w-6 trip-error-icon" />
        <span>Erro ao carregar a viagem. Tente novamente.</span>
      </ToolbarOnly>
    );
  }

  // ---------------------------------------------------------------------------
  // Dados derivados (só formatação de apresentação — INV-UI-TRAIL-001)
  // ---------------------------------------------------------------------------

  const STATUS_LABELS = {
    planejada: 'Planejada',
    em_andamento: 'Em andamento',
    fechamento_proposto: 'Fechamento proposto',
    fechada: 'Fechada',
    cancelada: 'Cancelada',
  };

  const userLabel = user?.full_name || user?.email || 'Usuário';

  let generatedAt;
  try { generatedAt = formatDateTime(new Date(), { omitSeconds: true }); }
  catch (_) { generatedAt = new Date().toLocaleString('pt-BR'); }

  // Consumo por motor: Object.entries(fuel_by_motor) ordenado desc (TripDetailPage l.822)
  const fuelByMotor = trip.actual?.fuel_by_motor || {};
  const fuelEntries = Object.entries(fuelByMotor).sort(([, a], [, b]) => b - a);
  const fuelChartData = fuelEntries.map(([key, litros]) => ({
    motor: motorLabel(key),
    litros: Math.round(litros),
  }));

  const stages = trip._stages || [];
  const hasStopReason = stages.some((s) => s.stop_reason);
  const opData = trip.operational_data || {};
  const crew = opData.crew || [];

  const briefing = weather?.briefing;

  // ---------------------------------------------------------------------------
  // Render principal
  // ---------------------------------------------------------------------------

  return (
    <div className="trip-print-overlay">
      <style>{TRIP_PRINT_CSS}</style>

      {/* Toolbar — visível só na tela */}
      <div className="trip-print-toolbar no-print">
        <div className="trip-toolbar-info">
          <strong>Relatório de Viagem</strong>
          <span className="trip-toolbar-sub">{trip.id} · {trip.equipment_name || trip.equipment_tag || ''}</span>
        </div>
        <div className="trip-toolbar-actions">
          {generateError && (
            <span className="trip-toolbar-error">{generateError}</span>
          )}
          <button
            type="button"
            className="print-btn print-btn--primary"
            onClick={generatePdf}
            disabled={generating}
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 trip-spin" />
                <span>Gerando PDF…</span>
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                <span>Baixar PDF</span>
              </>
            )}
          </button>
          <button
            type="button"
            className="print-btn print-btn--ghost"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft className="h-4 w-4" />
            Voltar
          </button>
        </div>
      </div>

      {/* Wrapper A4 retrato (210mm) — html2canvas captura este nó */}
      <div ref={wrapperRef} className="trip-page-wrapper">

        {/* ------------------------------------------------------------------ */}
        {/* 1. Cabeçalho                                                        */}
        {/* ------------------------------------------------------------------ */}
        <header className="trip-header">
          <div className="trip-header-brand">
            <img src="/assets/octopus-icon.png" alt="Octopus" className="trip-logo" />
            <div>
              <p className="trip-brand-name">Octopus</p>
              <p className="trip-brand-sub">Relatório de Viagem</p>
            </div>
          </div>
          <div className="trip-header-center">
            <p className="trip-equip-name">{trip.equipment_name || '—'}</p>
            <p className="trip-equip-tag">{trip.equipment_tag || '—'}</p>
            <p className="trip-vgm">{trip.id}</p>
          </div>
          <div className="trip-header-right">
            <p><span className="trip-key">Status:</span> {STATUS_LABELS[trip.status] || trip.status}</p>
            <p><span className="trip-key">Fuso:</span> {timezone}</p>
            <p><span className="trip-key">Gerado em:</span> {generatedAt}</p>
            <p><span className="trip-key">Por:</span> {userLabel}</p>
          </div>
        </header>

        <hr className="trip-divider" />

        {/* ------------------------------------------------------------------ */}
        {/* 2. Início / Fim / Duração                                           */}
        {/* ------------------------------------------------------------------ */}
        <section className="trip-section">
          <h2 className="trip-section-title">
            <FileText className="h-4 w-4" />
            Início / Fim / Duração
          </h2>
          <div className="trip-row-3">
            <div className="trip-kpi">
              <span className="trip-kpi-label">Início</span>
              <span className="trip-kpi-value">
                {trip.actual?.started_at
                  ? formatDateTime(trip.actual.started_at, { omitSeconds: true })
                  : '—'}
              </span>
            </div>
            <div className="trip-kpi">
              <span className="trip-kpi-label">Fim</span>
              <span className="trip-kpi-value">
                {trip.actual?.ended_at
                  ? formatDateTime(trip.actual.ended_at, { omitSeconds: true })
                  : '—'}
              </span>
            </div>
            <div className="trip-kpi">
              <span className="trip-kpi-label">Duração</span>
              <span className="trip-kpi-value">{fmtDuration(trip.actual?.duration_s)}</span>
            </div>
          </div>
        </section>

        <hr className="trip-divider" />

        {/* ------------------------------------------------------------------ */}
        {/* 3. Consumo por motor — Recharts BarChart (REQ-32)                   */}
        {/* ------------------------------------------------------------------ */}
        <section className="trip-section">
          <h2 className="trip-section-title">Consumo por Motor</h2>
          {fuelChartData.length > 0 ? (
            <>
              {/*
                Wrapper com largura fixa em px: elimina dependência do ResizeObserver
                do Recharts em contextos onde o container tem tamanho a ser medido.
                190px = aprox 190mm convertidos para 96dpi, mas limitado ao conteúdo.
                O wrapper de 210mm - 20mm padding = 190mm útil ≈ 718px.
              */}
              <div className="trip-chart-container">
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart
                    data={fuelChartData}
                    margin={{ top: 8, right: 20, bottom: 8, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="motor" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} unit=" L" />
                    <Tooltip
                      formatter={(v) => [
                        `${Number(v).toLocaleString('pt-BR')} L`,
                        'Consumo',
                      ]}
                    />
                    <Bar dataKey="litros" fill="#2563eb" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <table className="trip-table">
                <thead>
                  <tr>
                    <th>Motor</th>
                    <th>Consumo total</th>
                  </tr>
                </thead>
                <tbody>
                  {fuelEntries.map(([key, litros]) => (
                    <tr key={key}>
                      <td>{motorLabel(key)}</td>
                      <td>{fmtLitros(litros)}</td>
                    </tr>
                  ))}
                  {trip.actual?.fuel_total_l != null && (
                    <tr className="trip-table-total">
                      <td><strong>Total</strong></td>
                      <td><strong>{fmtLitros(trip.actual.fuel_total_l)}</strong></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          ) : (
            <p className="trip-empty">Sem dados de consumo por motor.</p>
          )}
        </section>

        <hr className="trip-divider" />

        {/* ------------------------------------------------------------------ */}
        {/* 4. Clima                                                             */}
        {/* ------------------------------------------------------------------ */}
        <section className="trip-section">
          <h2 className="trip-section-title">Clima</h2>
          {briefing ? (
            <div className="trip-clima">
              <div className="trip-row-2">
                <div>
                  <span className="trip-key">Situação: </span>
                  <span>{briefing.status || '—'}</span>
                </div>
                <div>
                  <span className="trip-key">Janela: </span>
                  <span>
                    {briefing.window && typeof briefing.window === 'object'
                      ? `${formatDateTime(briefing.window.start, { omitSeconds: true })} → ${formatDateTime(briefing.window.end, { omitSeconds: true })}`
                      : briefing.window || '—'}
                  </span>
                </div>
              </div>
              {briefing.alert && (
                <div className="trip-clima-alert">
                  <AlertCircle className="h-3 w-3" style={{ flexShrink: 0 }} />
                  <span>{briefing.alert_text || briefing.alert}</span>
                </div>
              )}
              {Array.isArray(briefing.locations) && briefing.locations.length > 0 && (
                <div>
                  <span className="trip-key">Localidades: </span>
                  <span>
                    {briefing.locations
                      .map((l) => (typeof l === 'string' ? l : l.name || l))
                      .join(', ')}
                  </span>
                </div>
              )}
              {weather?.archived && (
                <p className="trip-archived-note">
                  Briefing arquivado — viagem encerrada.
                </p>
              )}
            </div>
          ) : (
            <p className="trip-empty">Clima não disponível.</p>
          )}
        </section>

        <hr className="trip-divider" />

        {/* ------------------------------------------------------------------ */}
        {/* 5. Etapas / Paradas (_stages)                                        */}
        {/* ------------------------------------------------------------------ */}
        <section className="trip-section">
          <h2 className="trip-section-title">Etapas / Paradas</h2>
          {stages.length > 0 ? (
            <table className="trip-table">
              <thead>
                <tr>
                  <th>Atividade</th>
                  <th>Cargo</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Duração</th>
                  <th>Combustível</th>
                  {hasStopReason && <th>Motivo de parada</th>}
                </tr>
              </thead>
              <tbody>
                {stages.map((seg, idx) => {
                  const key = stageKey(seg);
                  const cs = cargoOf(seg);
                  return (
                    <tr key={idx}>
                      <td>{STAGE_LABEL[key] || seg.activity || '—'}</td>
                      <td>{cs === 'carregado' ? 'Carregado' : cs === 'vazio' ? 'Vazio' : '—'}</td>
                      <td>
                        {seg.started_at
                          ? formatDateTime(seg.started_at, { omitSeconds: true })
                          : '—'}
                      </td>
                      <td>
                        {seg.ended_at
                          ? formatDateTime(seg.ended_at, { omitSeconds: true })
                          : '—'}
                      </td>
                      <td>{fmtDuration(seg.duration_s)}</td>
                      <td>{fmtLitros(seg.fuel_l)}</td>
                      {hasStopReason && <td>{seg.stop_reason?.reason || '—'}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="trip-empty">Sem etapas registradas.</p>
          )}
        </section>

        <hr className="trip-divider" />

        {/* ------------------------------------------------------------------ */}
        {/* 6. Volume / Tripulação / Observações                                */}
        {/* ------------------------------------------------------------------ */}
        <section className="trip-section">
          <h2 className="trip-section-title">Volume / Tripulação / Observações</h2>
          <div className="trip-row-3">
            <div className="trip-kpi">
              <span className="trip-kpi-label">Volume</span>
              <span className="trip-kpi-value">
                {opData.volume_t != null ? `${opData.volume_t} t` : '—'}
              </span>
            </div>
            <div>
              <span className="trip-kpi-label">Tripulação</span>
              {crew.length > 0 ? (
                <ul className="trip-crew">
                  {crew.map((m, i) => (
                    <li key={i}>{m.nome} — {m.funcao}</li>
                  ))}
                </ul>
              ) : (
                <p className="trip-kpi-value">—</p>
              )}
            </div>
            <div>
              <span className="trip-kpi-label">Observações</span>
              <p className="trip-obs">{opData.observacoes || '—'}</p>
            </div>
          </div>
        </section>

        {/* Rodapé */}
        <footer className="trip-footer">
          <span>Octopus IoT • octopus.areia.com.br</span>
          <span>Gerado em {generatedAt} por {userLabel}</span>
        </footer>

      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CSS embutido
// ---------------------------------------------------------------------------
//
// Filosofia (espelhar EquipmentMonitorPrint.js):
//   - Na TELA: overlay full-screen com o wrapper A4 retrato (210mm) visível.
//     Recharts mede dimensões reais → SVGs pintados corretamente.
//   - O wrapper NÃO tem altura fixa (ao contrário do print de monitor): conteúdo
//     flui livremente e o generatePdf pagina conforme necessário.
//   - Tooltips do Recharts são escondidos para não aparecer congelados na captura.
//
const TRIP_PRINT_CSS = `
@keyframes trip-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
.trip-spin { animation: trip-spin 1s linear infinite; }

/* Overlay full-screen */
.trip-print-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.85);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: auto;
  padding: 0 0 32px 0;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}

/* Toolbar fixa no topo */
.trip-print-toolbar {
  position: sticky;
  top: 0;
  z-index: 2;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  background: #0f172a;
  color: #e2e8f0;
  border-bottom: 1px solid #1e293b;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  flex-wrap: wrap;
  box-sizing: border-box;
}
.trip-toolbar-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.trip-toolbar-info strong { font-size: 14px; }
.trip-toolbar-sub { font-size: 12px; color: #94a3b8; }
.trip-toolbar-actions { display: flex; gap: 8px; flex-shrink: 0; align-items: center; }
.trip-toolbar-error { font-size: 12px; color: #dc2626; margin-right: 8px; }

/* Botões da toolbar */
.print-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid transparent;
  cursor: pointer;
  transition: opacity 0.15s;
}
.print-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.print-btn--primary { background: #2563eb; color: white; }
.print-btn--primary:hover:not(:disabled) { background: #1d4ed8; }
.print-btn--ghost { background: transparent; color: #e2e8f0; border-color: #334155; }
.print-btn--ghost:hover { background: #1e293b; }

/* Estado centrado de loading/erro */
.trip-center-msg {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  color: #94a3b8;
  font-size: 15px;
}
.trip-error-icon { color: #dc2626; }

/* Wrapper da página A4 retrato — html2canvas captura este nó */
.trip-page-wrapper {
  width: 210mm;
  max-width: 210mm;
  /* Sem altura fixa: conteúdo flui; generatePdf pagina conforme necessário */
  margin: 20px auto 0 auto;
  padding: 10mm;
  box-sizing: border-box;
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  font-size: 9pt;
  line-height: 1.4;
}

/* Esconde tooltips do Recharts no overlay — evita congelamento na captura */
.trip-print-overlay .recharts-tooltip-wrapper,
.trip-print-overlay .recharts-tooltip-cursor,
.trip-print-overlay .recharts-active-dot {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
}
/* Bloqueia eventos de mouse para evitar hover no momento da captura */
.trip-print-overlay .recharts-wrapper {
  pointer-events: none !important;
}

/* Cabeçalho em 3 colunas */
.trip-header {
  display: grid;
  grid-template-columns: 1.1fr 1.2fr 1.1fr;
  gap: 6mm;
  align-items: center;
  padding-bottom: 0;
}
.trip-header-brand { display: flex; align-items: center; gap: 3mm; }
.trip-logo { width: 10mm; height: 10mm; object-fit: contain; flex-shrink: 0; }
.trip-brand-name { font-size: 11pt; font-weight: 700; margin: 0; letter-spacing: 0.3px; }
.trip-brand-sub { font-size: 8pt; color: #475569; margin: 0; }
.trip-header-center { text-align: center; }
.trip-equip-name { font-size: 12pt; font-weight: 700; margin: 0; }
.trip-equip-tag { font-size: 8pt; color: #475569; margin: 0.5mm 0; }
.trip-vgm { font-size: 8pt; font-family: ui-monospace, monospace; color: #64748b; margin: 0; }
.trip-header-right { text-align: right; font-size: 8pt; }
.trip-header-right p { margin: 0.3mm 0; }
.trip-key { font-weight: 600; }

/* Divisória */
.trip-divider { border: none; border-top: 1px solid #e2e8f0; margin: 3mm 0; }

/* Seção genérica */
.trip-section { margin-bottom: 0; }
.trip-section-title {
  display: flex;
  align-items: center;
  gap: 2mm;
  font-size: 10pt;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 2.5mm 0;
}

/* KPIs em 3 colunas */
.trip-row-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4mm; }
.trip-row-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 4mm; margin-bottom: 1.5mm; }

.trip-kpi { display: flex; flex-direction: column; gap: 1mm; }
.trip-kpi-label { font-size: 7.5pt; color: #64748b; font-weight: 600; }
.trip-kpi-value { font-size: 10pt; font-weight: 700; color: #0f172a; }

/* Gráfico de barras */
.trip-chart-container {
  width: 100%;
  height: 180px;
  margin-bottom: 3mm;
}

/* Tabela genérica */
.trip-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 8pt;
  margin-top: 2mm;
}
.trip-table th {
  text-align: left;
  font-weight: 600;
  color: #475569;
  border-bottom: 1.5px solid #e2e8f0;
  padding: 1.5mm 2mm;
  background: #f8fafc;
}
.trip-table td {
  padding: 1.5mm 2mm;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}
.trip-table-total td { border-top: 1.5px solid #e2e8f0; }

/* Clima */
.trip-clima { font-size: 8.5pt; display: flex; flex-direction: column; gap: 1.5mm; }
.trip-clima-alert {
  display: flex;
  align-items: flex-start;
  gap: 2mm;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 3px;
  padding: 1.5mm 2.5mm;
  font-size: 8pt;
}
.trip-archived-note { font-size: 7.5pt; color: #94a3b8; font-style: italic; margin: 0; }

/* Tripulação */
.trip-crew { margin: 1mm 0 0 0; padding: 0 0 0 3mm; list-style: disc; font-size: 8.5pt; }
.trip-crew li { margin: 0.5mm 0; }

/* Observações */
.trip-obs { font-size: 8.5pt; color: #374151; margin: 1mm 0 0 0; white-space: pre-wrap; }

/* Empty state */
.trip-empty { font-size: 8pt; color: #94a3b8; font-style: italic; margin: 2mm 0; }

/* Rodapé */
.trip-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 3mm;
  margin-top: 4mm;
  border-top: 1px solid #e2e8f0;
  font-size: 7.5pt;
  color: #64748b;
}

/* Oculta a toolbar no print (caso o browser envie p/ impressora) */
@media print {
  .no-print { display: none !important; }
  .trip-print-overlay {
    position: static !important;
    background: white !important;
    overflow: visible !important;
    padding: 0 !important;
  }
  .trip-page-wrapper {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    box-shadow: none !important;
  }
}
`;

export default TripReportPrint;
